import { ActionIcon, Button, Group } from "@mantine/core"
import { IconSettings } from "@tabler/icons-react"
import { Table } from "@tanstack/react-table"
import { useState } from "react"
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { DefaultModal } from "@components/default-modal"
import { DraggableTableHeader } from "./DraggableTableHeader"
import { useTranslation } from "react-i18next"

type Props<TData> = {
  table: Table<TData>
  onColumnOrderChange: (updater: (columnOrder: string[]) => string[]) => void
  onResetColumnOrder: () => void
  onResetColumnVisibility: () => void
}

export function ColumnSettingsMenu<TData>({
  table,
  onColumnOrderChange,
  onResetColumnOrder,
  onResetColumnVisibility,
}: Props<TData>) {
  const [columnSettingsModalOpen, setColumnSettingsModalOpen] = useState(false)

  const { t } = useTranslation("app_server")

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!active || !over || active.id === over.id) {
      return
    }

    onColumnOrderChange((columnOrder) => {
      const oldIndex = columnOrder.indexOf(active.id as string)
      const newIndex = columnOrder.indexOf(over.id as string)
      return arrayMove(columnOrder, oldIndex, newIndex)
    })
  }

  return (
    <>
      <div
        style={{
          position: "sticky",
          top: 2,
          left: 0,
          width: "100%",
          zIndex: 10,
          height: 0,
          display: "flex",
          justifyContent: "flex-end",
          pointerEvents: "none",
        }}
      >
        <ActionIcon
          variant="subtle"
          size={"sm"}
          onClick={() => setColumnSettingsModalOpen(true)}
          style={{
            marginRight: 8,
            pointerEvents: "auto",
          }}
        >
          <IconSettings />
        </ActionIcon>
      </div>
      <DefaultModal
        opened={columnSettingsModalOpen}
        onClose={() => setColumnSettingsModalOpen(false)}
      >
        <Group gap="xs">
          <Button size="sm" variant="subtle" onClick={onResetColumnOrder}>
            {t("TanStackTable_ResetColumnOrder")}
          </Button>
          <Button size="sm" variant="subtle" onClick={onResetColumnVisibility}>
            {t("TanStackTable_ResetColumnVisibility")}
          </Button>
        </Group>
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <SortableContext
            items={table.getState().columnOrder}
            strategy={verticalListSortingStrategy}
          >
            {table
              .getAllColumns()
              .sort((a, b) => {
                const aIndex = table.getState().columnOrder.indexOf(a.id)
                const bIndex = table.getState().columnOrder.indexOf(b.id)
                return aIndex - bIndex
              })
              .map((column) => (
                <DraggableTableHeader key={column.id} column={column} />
              ))}
          </SortableContext>
        </DndContext>
      </DefaultModal>
    </>
  )
}
